import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import Toast from '../components/Toast.jsx'
import BackButton from '../components/BackButton.jsx'
import { db } from '../data/localDb.js'
import { fetchFuncionarios, fetchMovimentacoes, fetchProdutos } from '../data/syncManager.js'
import { getProductTypeLabel } from '../data/products.js'

function formatDate(ts) {
  if (!ts) return '—'
  try {
    return new Date(ts).toLocaleDateString('pt-BR')
  } catch {
    return '—'
  }
}

export default function EmployeeHistoryPage() {
  const { id } = useParams()
  const [employee, setEmployee] = useState(null)
  const [movements, setMovements] = useState([])
  const [toast, setToast] = useState(null)
  const [toastType, setToastType] = useState('info')
  const [dias, setDias] = useState('90')
  const [expandedMovement, setExpandedMovement] = useState(null)

  useEffect(() => {
    async function load() {
      try {
        // Tentar buscar do banco local primeiro
        let funcs = await db.funcionarios.toArray()
        let emp = funcs.find(f => f.id === id)
        
        // Se não encontrar no local, buscar do Supabase
        if (!emp) {
          funcs = await fetchFuncionarios()
          emp = funcs.find(f => f.id === id)
        }
        
        if (!emp) {
          setToastType('error')
          setToast('Funcionário não encontrado.')
          return
        }
        setEmployee(emp)

        const d = Number(dias) || 90
        const desde = Date.now() - (d * 24 * 60 * 60 * 1000)

        // Tentar buscar movimentações do local primeiro
        let allMovs = await db.movimentacoes.toArray()
        
        // Se não tiver dados suficientes, buscar do Supabase
        if (allMovs.length === 0) {
          allMovs = await fetchMovimentacoes()
        }
        
        const movs = (allMovs || [])
          .filter(m => m.funcionarioId === id && (m.dataMovimentacao ?? m.criadoEm ?? 0) >= desde)
          .sort((a, b) => (b.dataMovimentacao ?? b.criadoEm ?? 0) - (a.dataMovimentacao ?? a.criadoEm ?? 0))

        // Enriquecer com dados dos produtos
        const prodIds = [...new Set(movs.map(m => m.produtoId))]
        let produtosAll = await db.produtos.toArray()
        
        // Se não tiver produtos no local, buscar do Supabase
        if (produtosAll.length === 0) {
          produtosAll = await fetchProdutos()
        }
        
        const prodMap = Object.fromEntries(produtosAll.filter(p => prodIds.includes(p.id)).map(p => [p.id, p]))

        setMovements(movs.map(m => ({
          ...m,
          produto: prodMap[m.produtoId]
        })))
      } catch (err) {
        setToastType('error')
        setToast(err?.message || 'Falha ao carregar histórico.')
      }
    }
    load()
  }, [id, dias])

  const resumo = useMemo(() => {
    const totalEntregas = movements.filter(m => m.tipoMovimentacao === 'saida-entrega').reduce((acc, m) => acc + (m.quantidade || 0), 0)
    const totalEmprestimos = movements.filter(m => m.tipoMovimentacao === 'saida-emprestimo').length
    const pendentes = movements.filter(m => m.status === 'pendente-devolucao').length
    return { totalEntregas, totalEmprestimos, pendentes }
  }, [movements])

  return (
    <div className="space-y-4">
      <Toast message={toast} type={toastType} onClose={() => setToast(null)} />

      <BackButton />

      {employee && (
        <div className="rounded-2xl cm-card p-4">
          <div className="text-sm font-semibold">{employee.nome}</div>
          <div className="mt-1 text-xs text-white/70">
            {employee.funcao || 'Sem função cadastrada'} • {employee.matricula || 'Sem matrícula'}
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2 text-center">
            <div className="rounded-lg bg-white/5 p-2">
              <div className="text-lg font-bold">{resumo.totalEntregas}</div>
              <div className="text-[10px] text-white/70">Itens Recebidos</div>
            </div>
            <div className="rounded-lg bg-white/5 p-2">
              <div className="text-lg font-bold">{resumo.totalEmprestimos}</div>
              <div className="text-[10px] text-white/70">Empréstimos</div>
            </div>
            <div className="rounded-lg bg-white/5 p-2">
              <div className="text-lg font-bold text-amber-400">{resumo.pendentes}</div>
              <div className="text-[10px] text-white/70">Pendentes</div>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-2xl cm-card p-3">
        <div className="text-xs font-semibold text-white/70 mb-2">Período</div>
        <select 
          value={dias} 
          onChange={(e) => setDias(e.target.value)}
          className="w-full rounded-xl bg-white/10 px-3 py-2 text-sm"
        >
          <option value="30">Últimos 30 dias</option>
          <option value="90">Últimos 90 dias</option>
          <option value="180">Últimos 6 meses</option>
          <option value="365">Último ano</option>
        </select>
      </div>

      <div className="space-y-3">
        {movements.length === 0 ? (
          <div className="rounded-2xl cm-card p-4 text-sm text-white/80">
            Nenhuma movimentação encontrada neste período.
          </div>
        ) : (
          movements.map((m) => {
            return (
              <div key={m.id} className="rounded-2xl cm-card p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold">{m.produtoNome}</div>
                    <div className="mt-1 text-xs text-white/70">
                      {m.produto ? getProductTypeLabel(m.produto.tipo) : 'Produto'} • {formatDate(m.dataMovimentacao)}
                    </div>
                    <div className="mt-1 text-xs">
                      <span className={m.tipoMovimentacao === 'saida-emprestimo' ? 'text-amber-400' : 'text-emerald-400'}>
                        {m.tipoMovimentacao === 'saida-emprestimo' ? '📦 Empréstimo' : '📤 Entrega'}
                      </span>
                      {' • '}
                      <span className={
                        m.status === 'concluido' ? 'text-emerald-400' :
                        m.status === 'pendente-devolucao' ? 'text-amber-400' :
                        m.status === 'perdido' ? 'text-red-400' :
                        'text-white/70'
                      }>
                        {m.status === 'concluido' ? 'Concluído' :
                         m.status === 'pendente-devolucao' ? 'Pendente' :
                         m.status === 'perdido' ? 'Perdido' :
                         m.status}
                      </span>
                    </div>
                    {m.quantidade > 1 && (
                      <div className="mt-1 text-xs text-white/70">Quantidade: {m.quantidade}</div>
                    )}
                    {(m.fotoEntrega || m.assinaturaFuncionario) && (
                      <button
                        onClick={() => setExpandedMovement(expandedMovement === m.id ? null : m.id)}
                        className="mt-2 text-xs text-accent-400 hover:text-accent-300"
                      >
                        {expandedMovement === m.id ? '▲ Ocultar comprovantes' : '▼ Ver comprovantes'}
                      </button>
                    )}
                    {expandedMovement === m.id && (
                      <div className="mt-3 space-y-3">
                        {m.fotoEntrega && (
                          <div>
                            <div className="text-xs text-white/60 mb-1">Foto da entrega</div>
                            <img
                              src={m.fotoEntrega}
                              alt="Foto da entrega"
                              className="w-full max-h-48 rounded-lg object-cover"
                            />
                          </div>
                        )}
                        {m.assinaturaFuncionario && (
                          <div>
                            <div className="text-xs text-white/60 mb-1">Assinatura do funcionário</div>
                            <img
                              src={m.assinaturaFuncionario}
                              alt="Assinatura"
                              className="w-full max-h-32 rounded-lg bg-white/10 object-contain"
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
